import React, { useCallback, useEffect, useRef, useState } from 'react';
// FIX: In v7, core declarative routing components are imported directly from 'react-router'
import { Routes, Route } from 'react-router';
import './App.css';

// Core layout layouts
import Navbar from './components/Navbar.jsx';
import Footer from './components/Footer.jsx';

// Page Views
import Home from './components/Home.jsx';
import Products from './components/Products.jsx';
import About from './components/About.jsx';
import Contact from './components/Contact.jsx';
import Exports from './components/Exports.jsx';
import PolicyPage from './components/PolicyPage.jsx';

const privacyPolicyContent = {
  title: 'Privacy Policy',
  intro: 'We value your trust and are committed to protecting your personal information while delivering a transparent and reliable experience.',
  sections: [
    {
      heading: 'Information We Collect',
      body: [
        'Contact details such as your name, email address, phone number, and business information when you inquire about products or services.',
        'Technical information such as browser type, IP address, and site usage data to improve our digital experience.'
      ]
    },
    {
      heading: 'How We Use Your Information',
      body: 'We use your information to respond to inquiries, process orders, provide quotations, and communicate updates about our products and services.'
    },
    {
      heading: 'Data Security',
      body: 'We take reasonable administrative and technical measures to protect your information against unauthorized access, loss, or misuse.'
    }
  ]
};

const returnPolicyContent = {
  title: 'Return Policy',
  intro: 'Our return approach is designed to be fair, transparent, and aligned with the needs of business customers and bulk orders.',
  sections: [
    {
      heading: 'Eligibility',
      body: [
        'Returns are considered only for products that are damaged, defective, or differ from the agreed specifications at the time of dispatch.',
        'Any return request must be raised within a reasonable period after delivery and supported by documentation.'
      ]
    },
    {
      heading: 'Return Process',
      body: 'Customers should contact our support team with the order reference, product details, and photographic evidence so we can review the request promptly.'
    },
    {
      heading: 'Resolution',
      body: 'Approved returns may be eligible for replacement, refund, or credit based on the nature of the issue and the agreed commercial terms.'
    }
  ]
};

const termsContent = {
  title: 'Terms & Conditions',
  intro: 'These terms define the responsibilities of Walnut Industries and our customers while using our website and placing business orders.',
  sections: [
    {
      heading: 'Use of Website',
      body: 'The website content is intended for informational and commercial purposes. Any use of the site should remain lawful and respectful of intellectual property rights.'
    },
    {
      heading: 'Orders & Pricing',
      body: [
        'All quotations and pricing are subject to change based on product specifications, quantity, and market conditions.',
        'Final commercial terms will be confirmed through written communication before order confirmation.'
      ]
    },
    {
      heading: 'Limitation of Liability',
      body: 'Walnut Industries will not be liable for indirect, incidental, or consequential damages arising from the use of our products or website beyond the scope of the agreed transaction.'
    }
  ]
};

const INTRO_STORAGE_KEY = 'walnut-intro-complete';
const MOBILE_BREAKPOINT = 768;

const App = () => {
  const [showIntro, setShowIntro] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(INTRO_STORAGE_KEY) !== 'true';
  });
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;
  });
  const videoRef = useRef(null);

  const completeIntro = useCallback(() => {
    window.localStorage.setItem(INTRO_STORAGE_KEY, 'true');
    setShowIntro(false);
  }, []);

  useEffect(() => {
    const introPlayed = window.localStorage.getItem(INTRO_STORAGE_KEY) === 'true';
    if (introPlayed) {
      setShowIntro(false);
      return;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const updateViewport = () => setIsMobile(mediaQuery.matches);
    updateViewport();

    const timer = window.setTimeout(completeIntro, 8000);

    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        window.clearTimeout(timer);
        completeIntro();
      });
    }

    mediaQuery.addEventListener?.('change', updateViewport);

    return () => {
      window.clearTimeout(timer);
      mediaQuery.removeEventListener?.('change', updateViewport);
    };
  }, [completeIntro]);

  return (
    <>
      {showIntro && (
        <div className="intro-overlay" role="presentation">
          <video
            ref={videoRef}
            className="intro-video"
            autoPlay
            muted
            playsInline
            preload="auto"
            onEnded={completeIntro}
            onError={completeIntro}
            onLoadedMetadata={() => {
              videoRef.current?.play().catch(() => completeIntro());
            }}
          >
            <source
              src={isMobile ? '/resources/mob%20animation.mp4' : '/resources/web%20animation.mp4'}
              type="video/mp4"
            />
          </video>
        </div>
      )}

      <div className={`app-shell ${showIntro ? 'is-intro-active' : ''}`}>
        {/* Navbar stays fixed across all pages */}
        <Navbar />

        {/* Dynamic page content switches here depending on the URL path */}
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<Products />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/exports" element={<Exports />} />
            <Route path="/privacy-policy" element={<PolicyPage {...privacyPolicyContent} />} />
            <Route path="/return-policy" element={<PolicyPage {...returnPolicyContent} />} />
            <Route path="/terms-conditions" element={<PolicyPage {...termsContent} />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default App;